import { Plus } from 'lucide-react';
import { useMemo } from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import { Button as CatalystButton } from '@/components/ui/catalyst/button';
import type {
  TreatmentRecordRequestSchema,
  TreatmentRecordRequestStatus,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

interface RecentRequestsListProps {
  matterId: string;
  providerId: string;
  onNewRequest: () => void;
}

const STATUS_COLORS: Record<
  TreatmentRecordRequestStatus,
  'zinc' | 'yellow' | 'blue' | 'green' | 'red' | 'purple' | 'amber'
> = {
  not_ready: 'zinc',
  pending: 'yellow',
  request_submitted: 'blue',
  request_received: 'blue',
  awaiting_invoice: 'amber',
  pending_payment: 'amber',
  records_pending: 'purple',
  records_delivered: 'green',
  records_ingested: 'green',
  rejected: 'red',
  cancelled: 'zinc',
  auto_retried: 'yellow',
};

const STATUS_LABELS: Record<TreatmentRecordRequestStatus, string> = {
  not_ready: 'Not Ready',
  pending: 'Pending',
  request_submitted: 'Submitted',
  request_received: 'Received',
  awaiting_invoice: 'Await Invoice',
  pending_payment: 'Pending Payment',
  records_pending: 'Records Pending',
  records_delivered: 'Delivered',
  records_ingested: 'Ingested',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  auto_retried: 'Auto Retried',
};

const formatDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const RequestRow = ({
  request,
}: {
  request: TreatmentRecordRequestSchema;
}) => {
  const status = request.status ?? 'pending';
  const {channel} = request;
  const channelType = channel?.type ?? null;
  const channelTarget =
    channel?.fax ?? channel?.email ?? channel?.phone_number ?? channel?.address ?? null;

  return (
    <div className="flex items-center gap-3 rounded-lg border border-zinc-100 bg-white px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {request.provided_records && request.provided_records.length > 0 && (
            <span className="text-xs font-medium text-zinc-700">
              {request.provided_records.join(', ')}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-zinc-500">
          {channelType && (
            <span className="capitalize">{channelType}</span>
          )}
          {channelTarget && (
            <>
              <span aria-hidden="true">&middot;</span>
              <span className="truncate">{channelTarget}</span>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <Badge color={STATUS_COLORS[status] ?? 'zinc'}>
          {STATUS_LABELS[status] ?? status}
        </Badge>
        <span className="text-xs text-zinc-400">
          {formatDate(request.modified_at)}
        </span>
      </div>
    </div>
  );
};

const RequestSkeleton = () => (
  <div className="space-y-2">
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="h-14 animate-pulse rounded-lg border border-zinc-100 bg-zinc-50"
      />
    ))}
  </div>
);

export const RecentRequestsList = ({
  matterId,
  providerId,
  onNewRequest,
}: RecentRequestsListProps) => {
  const { data: requests, isLoading } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsListQuery(
      { matterId, providerId },
      { pollingInterval: 10_000, skipPollingIfUnfocused: true },
    );

  const sortedRequests = useMemo(() => {
    if (!requests) return [];
    return [...requests].sort(
      (a, b) =>
        new Date(b.modified_at).getTime() - new Date(a.modified_at).getTime(),
    );
  }, [requests]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wide text-zinc-500">
          Record Requests
        </h3>
        <CatalystButton outline onClick={onNewRequest} className="gap-1.5">
          <Plus className="size-3.5" />
          Add Request
        </CatalystButton>
      </div>

      {isLoading && <RequestSkeleton />}

      {!isLoading && sortedRequests.length === 0 && (
        <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 py-6 text-center">
          <p className="text-sm text-zinc-500">No record requests yet.</p>
          <p className="mt-1 text-xs text-zinc-400">
            Create a request to start obtaining medical records.
          </p>
        </div>
      )}

      {!isLoading && sortedRequests.length > 0 && (
        <div className="space-y-1.5">
          {sortedRequests.map((request) => (
            <RequestRow key={request.id} request={request} />
          ))}
        </div>
      )}
    </div>
  );
};
